import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  Autocomplete,
  Typography,
  InputLabel,
  MenuItem,
  Select,
  IconButton,
  ListItem,
  ListItemText,
  InputAdornment,
  List,
  useTheme,
} from "@mui/material";
import {
  billAsTypes,
  ServiceCodes,
  StandardJobSearchResult,
} from "../../QueueBoard/models/workOrderTypes";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { useWorkOrderStore } from "../../store/zustandWorkOrderStore";
import { useEffect, useRef, useState } from "react";
import {
  ButtonStyle,
  ButtonStyleOutlined,
  searchListItem,
  standardJobSearchResultStyle,
} from "../../QueueBoard/models/workOrderStyles";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "notistack";
import { useLoading } from "components/common/context/LoadingContext";
import { SegmentApi } from "./api/segmentApi";
import SearchIcon from "@mui/icons-material/Search";
import { truncateName } from "components/common/Utils/commonUtilityFunctions";

import { logAmplitudeEventWithProps } from "components/common/amplitude/amplitude";
import { User } from "components/authentication/models/authTypes";
import packageJson from "../../../../package.json";
import localStorageHelper from "../../../app/localStorage";
import { ensureEditableWorkOrder } from "../utils/finalizationHelpers";
const SegmentSchema = Yup.object().shape({
  displayText: Yup.string().required("Segment title is required"),
  serviceCodeIdentity: Yup.number().required("Service code is required"),
  memo: Yup.string(),
  serviceValue: Yup.number().typeError("Service value must be a number"),
  serviceType: Yup.string(),
  stdJobCode: Yup.string(),
  stdJobDescription: Yup.string(),
  stdJobHeaderId: Yup.number().nullable(),
  stdJobRecommendedHrs: Yup.string(),
  stdJobSource: Yup.string(),
  stdJobSteps: Yup.string(),
  StdJobUnitModelId: Yup.number().nullable(),
});

interface SegmentProps {
  open: boolean;
  handleClose: () => void;
  push: (values: any) => void;
  invoiceDocumentIdentity?: number;
}

const Segment = ({
  open,
  handleClose,
  push,
  invoiceDocumentIdentity,
}: SegmentProps) => {
  const theme = useTheme();
  const [user] = useState<User | undefined>(
    localStorageHelper.getByKey("user")
  );
  const { serviceCodes, stdJobSources, standardJobCodes } = useWorkOrderStore();
  const [placeHolderForAmount] = useState("Estimated Labor Hours");
  const [stdJobSearchTerm, setStdJobSearchTerm] = useState("");
  const [showList, setShowList] = useState(false);
  const [stdJobResult, setStdJobResult] = useState<StandardJobSearchResult[]>(
    []
  );
  const [textForAmount, setTextForHours] = useState("Estimated Labor Hours");
  const [stdJobSource] = useState("manual");
  const { showLoading, hideLoading } = useLoading();
  const { enqueueSnackbar } = useSnackbar();
  const containerRef = useRef<HTMLDivElement>(null);
  const sortedStandardJobCodes = [...standardJobCodes].sort((a, b) =>
    a.value.localeCompare(b.value)
  );

  const [selectedJobCode, setSelectedJobCode] = useState(
    sortedStandardJobCodes[0].key
  );
  const handleSubmit = async (values) => {
    const canModify = await ensureEditableWorkOrder(
      invoiceDocumentIdentity,
      enqueueSnackbar
    );
    if (!canModify) {
      return;
    }
    push(values); // Add a new segment
    handleClose();

    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "handleNewSegmentSubmit",
      {
        elapsed_ms: "124",
        title: "New Segment",
        componentName: "WorkOrderSegment.tsx",
        version: packageJson.version,
      }
    );
  };
  const handleSearch = async () => {
    showLoading();
    try {
      const response = await SegmentApi.SearchStandardJobStep(
        stdJobSource,
        selectedJobCode,
        stdJobSearchTerm
      );
      if (response === null) setStdJobResult([]);
      else {
        setStdJobResult(response);
        setShowList(true);
      }
    } catch (error) {
      console.error("Error fetching standard jobs:", error);
    } finally {
      hideLoading();
    }

    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "searchStandardJobStep",
      {
        elapsed_ms: "124",
        title: "New Segment",
        componentName: "WorkOrderSegment.tsx",
        version: packageJson.version,
      }
    );
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowList(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const setFormikFieldValue = async (
    formik: any,
    fieldName: string,
    fieldValue: any
  ) => {
    await formik.setFieldValue(fieldName, fieldValue, true);
    await formik.setFieldTouched(fieldName, true, true);
    await formik.validateForm(); // Validate after setting all fields
  };
  const handleStandardJobSelection = async (
    stdJobStep: StandardJobSearchResult,
    formik: any
  ) => {
    await setFormikFieldValue(formik, "stdJobCode", stdJobStep.jobCode);
    await setFormikFieldValue(formik, "memo", stdJobStep.jobDescription);
    await setFormikFieldValue(
      formik,
      "stdJobHeaderId",
      stdJobStep.stdJobHeaderId
    );
    await setFormikFieldValue(
      formik,
      "stdJobRecommendedHrs",
      stdJobStep.durationHours
    );
    await setFormikFieldValue(formik, "stdJobSource", stdJobStep.source);
    await setFormikFieldValue(formik, "standardJobSteps", stdJobStep.jobSteps);
    await setFormikFieldValue(
      formik,
      "StdJobUnitModelId",
      stdJobStep.stdJobUnitModelId
    );
    await setFormikFieldValue(
      formik,
      "actualOrFlatRateLaborHrs",
      stdJobStep.durationHours
    );

    setStdJobSearchTerm(stdJobStep.jobCode);
    setStdJobResult([]);
    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "selectStandardJobStep",
      {
        elapsed_ms: "124",
        title: "New Segment",
        componentName: "WorkOrderSegment.tsx",
        version: packageJson.version,
      }
    );
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowList(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
      }}
    >
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.paper,
          },
        }}
      >
        <DialogTitle
          sx={(theme) => ({
            backgroundColor:
              theme.palette.mode === "light"
                ? theme.palette.primary.main
                : "#181F2A",
            color:
              theme.palette.mode === "light"
                ? theme.palette.primary.contrastText
                : "#fff",
          })}
        >
          {/* <Typography>New Segment</Typography> */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              pl="15px"
              sx={{
                fontStyle: "normal",
                fontWeight: 400,
                fontSize: "16px",
              }}
            >
              New Segment
            </Typography>

            <IconButton
              sx={{ color: theme.palette.primary.contrastText }}
              onClick={handleClose}
            >
              <CloseIcon sx={{ color: "white" }} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Formik
            initialValues={{
              displayText: "",
              serviceCodeIdentity: null,
              memo: "",
              billAs: "actual",
              actualOrFlatRateLaborHrs: 0.0,
              stdJobSource: "manual",
              stdJobCode: "",
              stdJobDescription: "",
              stdJobHeaderId: null,
              stdJobRecommendedHrs: 0.0,
              stdJobSteps: "",
              StdJobUnitModelId: null,
            }}
            validationSchema={SegmentSchema}
            onSubmit={handleSubmit}
          >
            {(formik) => (
              <Form>
                <Grid container spacing={2} sx={{ paddingTop: "10px" }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Display Text"
                      fullWidth
                      size="small"
                      variant="outlined"
                      inputProps={{ maxLength: 15 }}
                      name="displayText"
                      value={formik.values.displayText}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.displayText &&
                        !!formik.errors.displayText
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl
                      fullWidth
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: "10%" }}
                    >
                      <Autocomplete
                        options={serviceCodes}
                        size="small"
                        getOptionLabel={(option: ServiceCodes) =>
                          option.serviceCode.trim()
                        }
                        value={
                          serviceCodes.find(
                            (service) =>
                              Number(service.identity) ===
                              formik.values.serviceCodeIdentity
                          ) || null
                        }
                        id="serviceCodeIdentity"
                        onChange={(event, newValue) => {
                          formik.setFieldValue(
                            "serviceCodeIdentity",
                            newValue?.identity || null
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            error={
                              !!(
                                formik.touched.serviceCodeIdentity &&
                                formik.errors.serviceCodeIdentity
                              )
                            }
                            {...params}
                            label="Service Code*"
                            variant="outlined"
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl
                      fullWidth
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: "10%" }}
                    >
                      <InputLabel>Bill As</InputLabel>
                      <Select
                        onChange={(event) => {
                          formik.setFieldValue("billAs", event.target.value);
                          //
                          if (event.target.value === "actual") {
                            setTextForHours("Estimated Labor Hours");
                          } else {
                            setTextForHours("Flat Rate Labor Hours");
                          }
                        }}
                        label="Bill As"
                        name="billAs"
                        value={formik.values.billAs}
                      >
                        {billAsTypes.map((billAsObj) => (
                          <MenuItem key={billAsObj.key} value={billAsObj.key}>
                            {billAsObj.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <TextField
                        fullWidth
                        size="small"
                        label={textForAmount}
                        name="actualOrFlatRateLaborHrs"
                        type="number"
                        value={formik.values.actualOrFlatRateLaborHrs ?? 0.0}
                        onChange={formik.handleChange}
                        onFocus={(event) => {
                          if (Number(event.target.value) === 0) {
                            formik.setFieldValue(
                              "actualOrFlatRateLaborHrs",
                              ""
                            );
                          }
                        }}
                        onBlur={(event) => {
                          if (
                            event.target.value === "" ||
                            event.target.value === null
                          ) {
                            formik.setFieldValue("actualOrFlatRateLaborHrs", 0);
                          }
                          formik.handleBlur(event);
                        }}
                        placeholder={placeHolderForAmount}
                        inputProps={{ min: "0", step: "0.01" }} // Prevents negative values
                      />
                    </FormControl>
                  </Grid>

                  {/* Standard Job Source */}
                  <Grid item xs={12} md={3}>
                    <FormControl
                      fullWidth
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: "10%" }}
                    >
                      <InputLabel>Job Source</InputLabel>
                      <Select
                        name="stdJobSource"
                        label="Std Job Source"
                        value={formik.values.stdJobSource || ""}
                        onChange={(event) => {
                          formik.setFieldValue(
                            "stdJobSource",
                            event.target.value
                          );
                        }}
                      >
                        {stdJobSources.map((source, index) => (
                          <MenuItem key={index} value={source}>
                            {source}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Standard Job Search Type */}
                  <Grid item xs={12} md={3}>
                    <FormControl
                      fullWidth
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: "10%" }}
                    >
                      <InputLabel>Std Search Type</InputLabel>
                      <Select
                        value={selectedJobCode}
                        onChange={(e) => setSelectedJobCode(e.target.value)}
                        label="Std Search Type"
                      >
                        {sortedStandardJobCodes.map((code) => (
                          <MenuItem key={code.key} value={code.key}>
                            {code.value}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Standard Job Code Search */}
                  <Grid item xs={12} md={6}>
                    <FormControl
                      fullWidth
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: "10%" }}
                      ref={containerRef}
                    >
                      {/* <InputLabel>Enter min 3 characters</InputLabel> */}
                      <TextField
                        fullWidth
                        inputProps={{ minLength: 3, maxLength: 75 }}
                        label="Enter min 3 characters"
                        size="small"
                        name="stdJobCode"
                        variant="outlined"
                        value={stdJobSearchTerm || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          setStdJobSearchTerm(value);

                          if (value.trim() === "") {
                            setStdJobResult([]); //  clear results
                            setShowList(false); //  hide the list
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            // Prevent Formik from submitting the whole segment
                            // form when the user just wants to trigger a
                            // standard-job search via Enter.
                            e.preventDefault();
                            if (stdJobSearchTerm.trim().length >= 3) {
                              handleSearch();
                            }
                          }
                        }}
                        onFocus={() => {
                          if (
                            stdJobSearchTerm.trim().length > 0 &&
                            stdJobResult.length > 0
                          ) {
                            setShowList(true);
                          }
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                disabled={
                                  !stdJobSearchTerm ||
                                  stdJobSearchTerm.trim().length < 3
                                }
                                onClick={handleSearch}
                                edge="end"
                                aria-label="search"
                              >
                                <SearchIcon />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      {stdJobResult.length > 0 && showList && (
                        <List sx={standardJobSearchResultStyle(theme)}>
                          {stdJobResult.map(
                            (item: StandardJobSearchResult, index) => (
                              <ListItem
                                sx={searchListItem(theme)}
                                key={index}
                                onClick={() =>
                                  handleStandardJobSelection(item, formik)
                                }
                                divider
                              >
                                {/* <ListItemText primary={item} /> */}
                                <ListItemText
                                  primary={
                                    item.isModel
                                      ? item.jobCode + " - " + item.modelCode
                                      : item.jobCode
                                  }
                                  secondary={truncateName(
                                    item.jobDescription,
                                    50
                                  )}
                                  primaryTypographyProps={{
                                    fontWeight: "bold",
                                  }}
                                />
                              </ListItem>
                            )
                          )}
                        </List>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Memo"
                      name="memo"
                      inputProps={{ maxLength: 4000 }}
                      value={formik.values.memo ?? ""}
                      onChange={formik.handleChange}
                      multiline
                      rows={4}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Standard Job Steps"
                      name="standardJobSteps"
                      inputProps={{ maxLength: 4000 }}
                      value={formik.values.standardJobSteps ?? ""}
                      onChange={formik.handleChange}
                      multiline
                      rows={4}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
                <DialogActions sx={{ padding: "2px", mt: "5px" }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleClose}
                    color="inherit"
                    sx={ButtonStyleOutlined}
                  >
                    Cancel
                  </Button>

                  <Button
                    size="small"
                    // color="primary"
                    //sx={{ backgroundColor: "#181F2A" }}
                    sx={{ ...ButtonStyle }}
                    variant="contained"
                    type="submit"
                  >
                    Create Segment
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Segment;
